/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
// ANCHOR Utils
import { formatHasuraDate } from '../extras/formatHasuraDate';

// ANCHOR API
import { getCourseInvites } from './course-invite';

// ANCHOR Payloads
import {
  IGetActiveCourseStudentsPayload,
  IGetAllStudentProgress,
  IGetCourseStudentsPayload,
  IGetModuleItemCompleteProgress,
  IGetStudentProgress,
  IUpdateStudentPayload,
  IUpdateStudentProgress,
} from './payloads/course-student';
import {
  IBaseUpdateCourseStudentPayload,
  ICreateUserCertificate,
  IDistributeCertificate,
  IFetchUserCertificates,
  IUpdateStudentApprovalStatus,
  IUpdateUserCertificate,
} from './payloads/course';

// ANCHOR Results
import {
  ICourseStudentResult,
  IStudentCourseResult,
  IStudentProgressResult,
  APPROVAL_STATUS,
} from './results/course-student';
import { parseInfiniteData } from '../extras/parseInfiniteData';
import { IUserCertificateResult, PUBLISH_STATUS } from './results/course';
import { EnumNumberThree } from './results/ondemand';

export async function getCourseStudents(
  { courseId, token }: IGetCourseStudentsPayload,
): Promise<ICourseStudentResult[]> {
  const { sdk } = await import('../axios/graphql');
  const { courseStudent } = await sdk(token).GetCourseStudent({
    courseID: courseId,
  });

  return Promise.all(courseStudent.map(async (item) => {
    let approvalStatus: 0 | 1 | 2;

    if (item.approvalStatus !== 1 && item.userId) {
      const { userCertificates } = await sdk(token).GetUserCertificateByUserId({
        courseID: courseId,
        userID: item.userId,
      });

      if (userCertificates.length > 0) {
        const response = await sdk(token).UpdateStudentApprovalStatusByID({
          id: item.id,
        });

        if (response.courseStudent) {
          approvalStatus = response.courseStudent.approval_status as 1;
        }
      }
    }

    approvalStatus = item.approvalStatus as 0 | 1 | 2;

    return ({
      ...item,
      registeredAt: formatHasuraDate(item.registeredAt),
      startedAt: formatHasuraDate(item.startedAt),
      expiredAt: formatHasuraDate(item.expiredAt),
      approvalStatus: item.approvalStatus != null
        ? APPROVAL_STATUS[approvalStatus]
        : item.approvalStatus,
      courseModuleStudents: item.courseModuleStudents.map((courseModuleStudent) => ({
        ...courseModuleStudent,
        registeredAt: formatHasuraDate(courseModuleStudent.registeredAt),
        startedAt: formatHasuraDate(courseModuleStudent.startedAt),
        expiredAt: formatHasuraDate(courseModuleStudent.expiredAt),
      })),
    } as ICourseStudentResult);
  }));
}

export async function getActiveCourseStudentByUserAndCourseIDs(
  { courseId, userId, token }: IGetActiveCourseStudentsPayload,
) {
  const { sdk } = await import('../axios/graphql');
  const { courseStudents } = await sdk(token).GetActiveCourseStudent({
    courseID: Number(courseId),
    userID: userId,
  });

  const activeCourseStudent = courseStudents.length === 0
    ? undefined
    : courseStudents[0];

  return activeCourseStudent && ({
    ...activeCourseStudent,
    approvalStatus: activeCourseStudent.approvalStatus != null
      ? APPROVAL_STATUS[activeCourseStudent.approvalStatus as 0 | 1 | 2]
      : activeCourseStudent.approvalStatus,
  }) as ICourseStudentResult;
}

export async function updateCourseStudentStartTime(
  { studentId, token }: IUpdateStudentPayload,
) {
  const { sdk } = await import('../axios/graphql');

  await sdk(token).UpdateCourseStudentStart({
    id: studentId,
    startedAt: new Date().toISOString(),
  });
}

export async function updateCourseModuleStudentStartTime(
  { studentId, token }: IUpdateStudentPayload,
) {
  const { sdk } = await import('../axios/graphql');

  await sdk(token).UpdateCourseModuleStudentStart({
    id: studentId,
    startedAt: new Date().toISOString(),
  });
}

export async function getCourseStudentsRow(
  { courseId, token }: IGetCourseStudentsPayload,
) {
  const students = await getCourseStudents({
    courseId,
    token,
  });

  const activeStudents = students.filter((student) => (
    student.isActive
  ));

  const invites = await getCourseInvites({
    courseId,
    token,
  });

  const pendingInvites = invites.filter((invite) => (
    !invite.isUsed
  ));

  return {
    invites,
    students,
    pendingInvites,
    activeStudents,
    combined: [...activeStudents, ...pendingInvites],
  };
}

export async function getStudentCourses(
  { studentId, token }: IUpdateStudentPayload,
): Promise<IStudentCourseResult[]> {
  const { sdk } = await import('../axios/graphql');
  const { courseStudent } = await sdk(token).GetStudentCourses({
    userId: studentId,
  });

  return courseStudent.map((student) => ({
    ...student.course,
    publishStatus: PUBLISH_STATUS[student.course?.publishStatus as EnumNumberThree],
    videoHours: student.course?.videoHour.aggregate?.count,
    moduleCount: student.course?.moduleCount.aggregate?.count,
    studentCount: 0,
    approvalStatus: student.approvalStatus != null
      ? APPROVAL_STATUS[student.approvalStatus as EnumNumberThree]
      : student.approvalStatus,
    objectives: [],
    gains: [],
    requirements: [],
    lecturers: [],
    cpdUnitRewards: [],
    industries: [],
    missingItems: student.studentProgressAggregate.aggregate?.count,
    hasStarted: !!student.startedAt,
    isPinned: false,
  }) as IStudentCourseResult);
}

export async function getAllStudentProgress(
  { token, studentId }: IGetAllStudentProgress,
): Promise<IStudentProgressResult[]> {
  const { sdk } = await import('../axios/graphql');
  const { studentProgress } = await sdk(token).GetCourseStudentProgress({
    studentID: studentId,
  });

  return studentProgress.map((item) => ({
    ...item,
    isComplete: !!item.isComplete,
  }) as IStudentProgressResult);
}

export async function getOrCreateStudentProgress(
  { token, studentId, moduleItemId }: IGetStudentProgress,
): Promise<IStudentProgressResult> {
  const { sdk } = await import('../axios/graphql');
  const { studentProgress } = await sdk(token).GetStudentProgess({
    courseStudentId: studentId,
    moduleItemId,
  });

  if (studentProgress.length > 0) {
    return studentProgress[0] as IStudentProgressResult;
  }

  const { studentProgress: newStudentProgress } = await sdk(token).CreateStudentProgess({
    courseStudentId: studentId,
    moduleItemId,
  });

  return newStudentProgress as IStudentProgressResult;
}

export async function completeStudentModuleItem(
  { token, progressId }: IUpdateStudentProgress,
): Promise<IStudentProgressResult> {
  const { sdk } = await import('../axios/graphql');
  const { studentProgress } = await sdk(token).UpdateStudentProgressStatus({
    id: progressId,
    isComplete: true,
  });

  return studentProgress as IStudentProgressResult;
}

export async function incompleteStudentModuleItem(
  { token, progressId }: IUpdateStudentProgress,
): Promise<IStudentProgressResult> {
  const { sdk } = await import('../axios/graphql');
  const { studentProgress } = await sdk(token).UpdateStudentProgressStatus({
    id: progressId,
    isComplete: false,
  });

  return studentProgress as IStudentProgressResult;
}

export async function updateStudentProgressAsLocked(
  { token, progressId }: IUpdateStudentProgress,
) {
  const { sdk } = await import('../axios/graphql');
  await sdk(token).UpdateCourseStudentProgressLockStatus({
    id: progressId,
    isLocked: true,
  });
}

export async function updateStudentProgressAsUnlocked(
  { token, progressId }: IUpdateStudentProgress,
) {
  const { sdk } = await import('../axios/graphql');
  await sdk(token).UpdateCourseStudentProgressLockStatus({
    id: progressId,
    isLocked: false,
  });
}

export async function getModuleItemCompleteProgress(
  { moduleItemId, token }: IGetModuleItemCompleteProgress,
): Promise<IStudentProgressResult[] | undefined> {
  const { sdk } = await import('../axios/graphql');
  const { studentProgress } = await sdk(token).GetModuleItemCompleteProgress({
    moduleItemId,
  });

  return studentProgress as IStudentProgressResult[];
}

export async function getCourseStudentById({
  token, studentId,
}: IGetAllStudentProgress) {
  const { sdk } = await import('../axios/graphql');

  const { student } = await sdk(token).GetCourseStudentById({
    id: studentId,
  });

  return {
    ...student,
    approvalStatus: student?.approvalStatus != null
      ? APPROVAL_STATUS[student?.approvalStatus as 0 | 1 | 2]
      : student?.approvalStatus,
  } as ICourseStudentResult;
}

export async function approveCourseStudents(
  {
    certificateTemplateIDs, courseID, userIDs, token,
  }: IDistributeCertificate,
) {
  const { sdk } = await import('../axios/graphql');

  const { students } = await sdk(token).GetCourseStudentDetailsByIDs({
    ids: userIDs,
  });

  const approvedStudents = students.map((student) => ({
    id: student.id,
    approval_status: 1,
    user_id: student.userId,
    course_id: student.courseId,
    expired_at: student.expiredAt,
    has_proceeding_access: student.hasProceedingAccess,
    is_active: student.isActive,
    order_id: student.orderId,
    registered_at: student.registeredAt,
    started_at: student.startedAt,
  }));

  await sdk(token).UpdateStudentApprovalStatusByIDs({
    objects: approvedStudents,
  });

  const userList = students
    .map((student) => student.userId as string | undefined)
    .filter(Boolean) as string[];

  const { user } = await sdk(token).GetBasicUserDetailsByIDs({
    ids: userList,
  });

  const userCertificates = user.map(({ displayName, id }) => (
    certificateTemplateIDs.map((certificateID) => ({
      certificate_template_id: certificateID,
      user_name: displayName,
      user_id: id,
      course_id: courseID,
    }))
  ));

  await sdk(token).CreateUserCertificates({
    objects: parseInfiniteData(userCertificates),
  });
}

export async function removeCourseStudents(
  { userIDs, token }: IUpdateStudentApprovalStatus,
) {
  const { sdk } = await import('../axios/graphql');

  const { students } = await sdk(token).GetCourseStudentDetailsByIDs({
    ids: userIDs,
  });

  const rejectedStudents = students.map((student) => ({
    id: student.id,
    approval_status: 2,
    user_id: student.userId,
    course_id: student.courseId,
    expired_at: student.expiredAt,
    has_proceeding_access: student.hasProceedingAccess,
    is_active: false,
    order_id: student.orderId,
    registered_at: student.registeredAt,
    started_at: student.startedAt,
  }));

  await sdk(token).UpdateStudentApprovalStatusByIDs({
    objects: rejectedStudents,
  });
}

export async function getCourseStudentCertificates({
  token, id, courseId,
}: IFetchUserCertificates) {
  const { sdk } = await import('../axios/graphql');
  const { userCertificates } = await sdk(token).GetUserCertificateByUserId({
    userID: id,
    courseID: courseId,
  });

  return userCertificates as unknown as IUserCertificateResult[];
}

export async function getUserCertificates({
  token, id,
}: IBaseUpdateCourseStudentPayload) {
  const { sdk } = await import('../axios/graphql');
  const { userCertificates } = await sdk(token).GetUserCertificates({
    userID: id,
  });

  return userCertificates as unknown as IUserCertificateResult[];
}

interface IIDProps {
  id: string;
}

export async function getCourseStudentCertificateById({
  id,
}: IIDProps) {
  const { sdk } = await import('../axios/graphql');
  const { userCertificate } = await sdk().GetUserCertificateById({
    id,
  });

  if (userCertificate) {
    return userCertificate as unknown as IUserCertificateResult;
  }

  return undefined;
}

export async function createCourseStudentCertificate({
  token, userID, certificateTemplateIDs, courseID,
}: ICreateUserCertificate) {
  const { sdk } = await import('../axios/graphql');
  const { user } = await sdk(token).GetUserById({
    id: userID,
  });

  if (user) {
    const userCertificates = certificateTemplateIDs.map((certificateID) => ({
      certificate_template_id: certificateID,
      user_name: user.displayName,
      user_id: user.id,
      course_id: courseID,
    }));

    await sdk(token).CreateUserCertificates({
      objects: userCertificates,
    });
  }
}

export async function updateCourseStudentCertificate({
  token, userCertificates, userName,
}: IUpdateUserCertificate) {
  const { sdk } = await import('../axios/graphql');

  const newUserCertificates = userCertificates.map((userCertificate) => ({
    certificate_template_id: userCertificate.certificateTemplate.id,
    user_name: userName,
    user_id: userCertificate.userId,
    id: userCertificate.id,
  }));

  await sdk(token).CreateUserCertificates({
    objects: newUserCertificates,
  });
}

export async function deleteCourseStudentCertificate({
  token, id,
}: IBaseUpdateCourseStudentPayload) {
  const { sdk } = await import('../axios/graphql');
  const { userCertificate } = await sdk(token).DeleteUserCertificateById({
    id,
  });

  return userCertificate;
}
